import React, { useEffect, useMemo, useRef, useState } from "react";
import { View, StyleSheet, Keyboard, Pressable } from "react-native";
import { useNavigation } from "@react-navigation/native";

import HeaderWithBackAndTitle from "@components/HeaderWithBackAndTitle";
import ToastMessage from "@components/Toast";

// eslint-disable-next-line import/no-named-as-default
import t from "@constants/localization";
import color from "@constants/color";

import OTPInput from "./OTPInput";
import CompleteDelivery, {
  CompleteDeliveryData,
  DEFAULT_COMPLETE_DELIVERY_DATA,
} from "./CompleteDelivery";
import LinearGradient from "react-native-linear-gradient";
import LoadingModal from "@components/ModalLoader";
import { useDispatch, useSelector } from "react-redux";
import { getDeliveryOtpViaSms, markMultipleDeliveries } from "@services/api";
import { useCachedLocation } from "@components/common/LocationHook";
import { actions as myTaskActions } from "@reducers/myTaskReducer";
import PaymentComponent from "./PaymentComponent";
import useCountDown from "react-countdown-hook";
import OtpNumberModal from "@screens/RRD/OtpNumberModal";
import Text from "@components/CustomText";
import RenderHtml from "react-native-render-html";
import { width } from "@utils/metrics";
import ConfirmationModalWithImage from "@components/ConfirmationModalWithImage";
import ModalCancel from "@assets/svg/MyTasks/ModalCancel";
import OtpRequestButton from "../components/OtpRequestButton";
import { buildDeliveryProofPayload } from "@screens/MyTasks/utils";

interface RouteParams {
  otpLength: number;
  otpText: string;
  online_payment_enabled: boolean;
  collectible_amount: string;
  cx_user_id: string;
  paymentMode: string;
  phone_numbers: string[];
  masked_phone_numbers?: string[];
  extra_info: string;
  status: string;
  amount: string;
  paytm_order_id: string;
  payout_plan: unknown;
  enable_mark_without_otp?: boolean;
  cancel_del_incentive_msg?: string;
  send_cancel_delivery_otp?: boolean;
  is_otp_request_btn_enabled?: boolean;
  otp_request_reasons?: Array<{ key: string; label: string }>;
  order_ids?: number[];
  delivery_date?: string;
  cx_phone_number?: string;
  plastic_bag?: {
    opted_in?: boolean;
    delivered?: boolean;
    count?: number;
    image_url?: string | null;
    delivered_count?: number | null;
    [key: string]: unknown;
  } | null;
  delivery_proof?: {
    popup_variant: "POLYBAG" | "ITEM_VERIFICATION";
    images: Array<{
      url: string;
      lat: number | null;
      lng: number | null;
      uploaded_at: string;
    }>;
  } | null;
}

interface Props {
  route: {
    params: RouteParams;
  };
}

interface ResendOtpHelperProps {
  phone_numbers: string[];
  setShowNumberModal: (show: boolean) => void;
  resendOtp: (phoneNumber: string) => Promise<void>;
}

interface PrimaryResendOTPLineProps {
  phone_numbers: string[];
  showResend: boolean;
  setShowNumberModal: (show: boolean) => void;
  resendOtp: (phoneNumber: string) => Promise<void>;
  selectedPhoneNumber: string;
}

interface SecondaryResendOTPLineProps {
  showResend: boolean;
  isCountDownRunning: boolean;
  countDown: number;
  setShowNumberModal: (show: boolean) => void;
  phone_numbers: string[];
  resendOtp: (phoneNumber: string) => Promise<void>;
}

function resendOtpHelper({
  phone_numbers: phoneNums,
  setShowNumberModal,
  resendOtp: doResendOtp,
}: ResendOtpHelperProps) {
  if (!phoneNums?.length) return null;
  if (phoneNums?.length > 1) {
    setShowNumberModal(true);
    return;
  }
  doResendOtp(phoneNums[0]);
}

function PrimaryResendOTPLine({
  phone_numbers: phoneNums,
  masked_phone_numbers: maskedNums,
  showResend,
  setShowNumberModal,
  resendOtp: doResendOtp,
  selectedPhoneNumber,
}: PrimaryResendOTPLineProps & { masked_phone_numbers?: string[] }) {
  const localDisplayPhoneNumbers =
    maskedNums && maskedNums.length ? maskedNums : phoneNums;
  return !showResend ? (
    <Pressable
      style={styles.resendOtpContainer}
      onPress={() => {
        resendOtpHelper({
          phone_numbers: phoneNums,
          setShowNumberModal,
          resendOtp: doResendOtp,
        });
      }}
      testID="resendOtpSingleNumber"
    >
      <Text
        newfont
        variant="body_text_4"
        style={styles.resendOtpText}
      >{`मैसेज के माध्यम से OTP भेजें `}</Text>
      <Text newfont variant="body_text_4" style={styles.phoneNumberText}>{`${
        localDisplayPhoneNumbers?.length
          ? localDisplayPhoneNumbers?.length > 1
            ? ""
            : `(फ़ोन : ${localDisplayPhoneNumbers[0]})`
          : ""
      }`}</Text>
    </Pressable>
  ) : (
    <View style={styles.centeredContainer}>
      <Text
        newfont
        variant="body_text_3"
      >{`OTP ${selectedPhoneNumber} पर भेजा गया`}</Text>
    </View>
  );
}

function SecondaryResendOTPLine({
  showResend,
  isCountDownRunning,
  countDown: countDownVal,
  setShowNumberModal,
  phone_numbers: phoneNums,
  resendOtp: doResendOtp,
}: SecondaryResendOTPLineProps) {
  return showResend ? (
    <View style={styles.rowCentered}>
      <Text newfont variant="body_text_3" style={styles.mediumBlackText}>
        OTP नहीं मिला?
      </Text>
      {isCountDownRunning ? (
        <Text newfont variant="body_text_2" style={styles.mediumBlackText}>
          दोबारा भेजें
        </Text>
      ) : null}
      <Pressable
        onPress={() => {
          if (isCountDownRunning) return;
          resendOtpHelper({
            phone_numbers: phoneNums,
            setShowNumberModal,
            resendOtp: doResendOtp,
          });
        }}
        testID="resendOtpSingleNumber"
      >
        <Text
          newfont
          variant="body_text_2"
          style={[
            styles.resendActionText,
            isCountDownRunning && styles.countdownText,
          ]}
        >
          {isCountDownRunning ? `${countDownVal / 1000} sec` : "दोबारा भेजें"}
        </Text>
      </Pressable>
      {isCountDownRunning ? (
        <Text newfont variant="body_text_2" style={styles.mediumBlackText}>
          में
        </Text>
      ) : null}
    </View>
  ) : null;
}

const OTP_PALETTE = {
  headerBg: "#fff",
  incentiveBannerBg: "#E8FFA5",
  markWithoutOtpBg: "#F8F8F8",
  shadowColor: "#000",
};

const NewOTPVerification: React.FC<Props> = ({ route }) => {
  const params = route.params || {};
  const PATH = "screens/MyTask";
  const {
    otpLength,
    otpText,
    online_payment_enabled,
    collectible_amount,
    cx_user_id,
    paymentMode,
    phone_numbers,
    masked_phone_numbers = [],
    extra_info,
    status,
    amount,
    paytm_order_id,
    payout_plan,
    enable_mark_without_otp = false,
    cancel_del_incentive_msg = "",
    send_cancel_delivery_otp = false,
    is_otp_request_btn_enabled = false,
    otp_request_reasons = [],
    order_ids = [],
    delivery_date = "",
    cx_phone_number = "",
    plastic_bag = null,
    delivery_proof = null,
  } = params;
  const navigation = useNavigation();
  const [otp, setOtp] = useState("");
  const [otpMsgText, setOtpMsgText] = useState(otpText || "");
  const [showInstantCommissionModal, setShowInstantCommissionModal] =
    useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [completeDeliveryData, setCompleteDeliveryData] =
    useState<CompleteDeliveryData>(DEFAULT_COMPLETE_DELIVERY_DATA);
  const { orderMarkingMap } = useSelector(
    (state: { myTaskReducer: { orderMarkingMap: Record<string, unknown> } }) =>
      state.myTaskReducer,
  );
  // Cache-only, matching CustomerOrderView: this is the same delivery one
  // screen later, so it must not wake GPS either. Reads state.location.lastFix
  // (session-scoped, non-persisted) — never captureCurrentPosition.
  const getCachedLocation = useCachedLocation();
  const [countDown, { start, reset }] = useCountDown(30 * 1000, 1000);
  const [showResend, setShowResend] = useState(false);
  const [showNumberModal, setShowNumberModal] = useState(false);
  const [selectedPhoneNumber, setSelectedPhoneNumber] = useState("");
  const resendOtpBtnLoadingRef = useRef(false);
  const dispatch = useDispatch();
  const [otpError, setOtpError] = useState(false);
  const [showCancleMarkingModal, setShowCancleMarkingModal] = useState(false);

  const displayPhoneNumbers =
    masked_phone_numbers && masked_phone_numbers.length
      ? masked_phone_numbers
      : phone_numbers;

  const handleVerification = async (): Promise<void> => {
    setIsLoading(true);
    try {
      const coords = getCachedLocation();
      const payload = {
        orders: Object.values(orderMarkingMap),
        otp: `${otp}`,
        lat: coords.latitude,
        lng: coords.longitude,
        otp_enabled: true,
        online_payment_enabled: online_payment_enabled,
        collectible_amount: parseFloat(collectible_amount),
        cx_user_id: cx_user_id,
        status,
        amount,
        paytm_order_id,
        payment_mode: paymentMode,
        payout_plan,
        send_cancel_delivery_otp,
        ...buildDeliveryProofPayload({
          plasticBag: plastic_bag,
          deliveryProof: delivery_proof,
        }),
      };

      const response = await markMultipleDeliveries(payload);

      if (response?.data?.status === 200) {
        dispatch(myTaskActions.clearOrderMarking());
        if (response?.data?.payload?.type === "INSTANT_COMMISSION_TRIGGERED") {
          setCompleteDeliveryData({
            ...DEFAULT_COMPLETE_DELIVERY_DATA,
            ...(response?.data?.payload?.payoutBreakdown || {}),
          });
          setShowInstantCommissionModal(true);
          return;
        }
        ToastMessage("success", response?.data?.payload?.toastMessage);
        navigation.pop(2);
      } else {
        setOtpError(true);
        setOtpMsgText(response?.data?.payload?.errorText);
      }
    } catch (error: unknown) {
      setOtpMsgText((error as Error).message);
      setOtpError(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleMarkWithoutOtp = async (): Promise<void> => {
    if (isLoading) return;
    setIsLoading(true);
    try {
      const coords = getCachedLocation();
      const payload = {
        orders: Object.values(orderMarkingMap),
        otp: "",
        lat: coords.latitude,
        lng: coords.longitude,
        otp_enabled: false,
        online_payment_enabled: online_payment_enabled,
        collectible_amount: parseFloat(collectible_amount),
        cx_user_id: cx_user_id,
        status,
        amount,
        paytm_order_id,
        payment_mode: paymentMode,
        payout_plan,
        mark_cancel_del_without_otp: true,
        send_cancel_delivery_otp,
        ...buildDeliveryProofPayload({
          plasticBag: plastic_bag,
          deliveryProof: delivery_proof,
        }),
      };

      const response = await markMultipleDeliveries(payload);

      if (response?.data?.status === 200) {
        dispatch(myTaskActions.clearOrderMarking());
        if (response?.data?.payload?.type === "INSTANT_COMMISSION_TRIGGERED") {
          setCompleteDeliveryData({
            ...DEFAULT_COMPLETE_DELIVERY_DATA,
            ...(response?.data?.payload?.payoutBreakdown || {}),
          });
          setShowInstantCommissionModal(true);
          return;
        }
        ToastMessage("success", response?.data?.payload?.toastMessage);
        navigation.pop(2);
      } else {
        setOtpError(true);
        setOtpMsgText(response?.data?.payload?.errorText);
      }
    } catch (error: unknown) {
      setOtpMsgText((error as Error).message);
      setOtpError(true);
    } finally {
      setIsLoading(false);
    }
  };

  const isCountDownRunning = useMemo(() => {
    return countDown > 0;
  }, [countDown]);

  const resendOtp = async (phoneNumber: string): Promise<void> => {
    if (resendOtpBtnLoadingRef.current) return;
    resendOtpBtnLoadingRef.current = true;
    try {
      const phoneIndex = phone_numbers.indexOf(phoneNumber);
      const maskedNumber = masked_phone_numbers?.[phoneIndex] || phoneNumber;
      setSelectedPhoneNumber(maskedNumber);

      const response = await getDeliveryOtpViaSms(
        phoneNumber,
        Object.keys(orderMarkingMap),
        collectible_amount,
        send_cancel_delivery_otp,
      );
      ToastMessage(
        "success",
        response?.data?.toastMessage || "OTP Sent Successfully",
        1500,
      );
      reset();
      start();
      setShowResend(true);
    } catch {
      ToastMessage("error", "Try Again", 1500);
    } finally {
      resendOtpBtnLoadingRef.current = false;
      setShowNumberModal(false);
    }
  };

  const handleOtpChange = (value: string): void => {
    if (otpError) {
      setOtpError(false);
      setOtpMsgText(otpText || "");
    }
    setOtp(value);
  };

  useEffect(() => {
    if (otp && otp.length === otpLength) {
      Keyboard.dismiss();
      handleVerification();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otp]);

  return (
    <View style={styles.container}>
      <HeaderWithBackAndTitle
        headertitle={t("screens/RRD/OTPVerification/header")}
        subHeaderTitle=""
        headerStyle={styles.headerWhiteBg}
        navigation={navigation}
        route={route}
        hideShadow={false}
        rightIcon={null}
        RightButton={null}
        bottomComponent={undefined}
      />
      {cancel_del_incentive_msg && enable_mark_without_otp ? (
        <View style={styles.incentiveBannerWrapper}>
          <View style={styles.incentiveBanner}>
            <RenderHtml
              html={cancel_del_incentive_msg}
              imagesMaxWidth={width}
              tagsStyles={{
                body: {
                  margin: 0,
                  padding: 0,
                  textAlign: "center",
                },
                p: {
                  fontSize: 16,
                  margin: 0,
                  padding: 0,
                  textAlign: "center",
                },
              }}
            />
          </View>
        </View>
      ) : null}
      <LinearGradient
        colors={["rgba(99, 99, 99, 1)", "rgba(18, 18, 18, 1)"]}
        start={{ x: 0.0, y: 0.1 }}
        end={{ x: 0.9, y: 0.4 }}
        style={styles.innerContainer}
      >
        <PaymentComponent
          collectible_amount={collectible_amount}
          paymentMode={paymentMode}
        />
        <OTPInput
          value={otp}
          valueLength={otpLength}
          onChange={handleOtpChange}
          otpText={otpMsgText}
          error={otpError}
        />
      </LinearGradient>
      {extra_info ? (
        <Text newfont variant="body_text_3" style={styles.extraInfoText}>
          {extra_info}
        </Text>
      ) : null}
      <PrimaryResendOTPLine
        {...{
          phone_numbers,
          masked_phone_numbers,
          showResend,
          resendOtp,
          selectedPhoneNumber,
          setShowNumberModal,
        }}
      />
      <SecondaryResendOTPLine
        {...{
          showResend,
          isCountDownRunning,
          countDown,
          setShowNumberModal,
          resendOtp,
          phone_numbers,
        }}
      />

      {enable_mark_without_otp ? (
        <View style={styles.markWithoutOtpContainer}>
          <Pressable
            style={styles.markWithoutOtpButton}
            onPress={() => {
              if (isLoading) return;
              setShowCancleMarkingModal(true);
            }}
            disabled={isLoading}
          >
            <Text
              newfont
              variant="body_text_3"
              style={styles.markWithoutOtpText}
            >
              {`OTP के बिना मार्क करें`}
            </Text>
          </Pressable>
        </View>
      ) : null}
      <View style={styles.otpRequestBtnContainer}>
        <OtpRequestButton
          isEnabled={is_otp_request_btn_enabled}
          reasons={otp_request_reasons}
          orderIds={order_ids}
          deliveryDate={delivery_date}
          cxId={cx_user_id}
          cxPhoneNumber={cx_phone_number}
        />
      </View>
      {showInstantCommissionModal && (
        <CompleteDelivery
          isVisible={showInstantCommissionModal}
          onClose={() => {
            setShowInstantCommissionModal(false);
            navigation.pop(2);
          }}
          completeDeliveryData={completeDeliveryData}
        />
      )}
      <ConfirmationModalWithImage
        imageComponent={<ModalCancel />}
        text={t(`${PATH}/mark_without_otp_modal_text`)}
        visible={showCancleMarkingModal}
        setVisible={setShowCancleMarkingModal}
        buttonText={[
          {
            text: t(`${PATH}/no`),
            type: "cancel",
            onPress: () => setShowCancleMarkingModal(false),
          },
          {
            text: t(`${PATH}/yes`),
            type: "submit",
            onPress: () => {
              setShowCancleMarkingModal(false);
              handleMarkWithoutOtp();
            },
          },
        ]}
      />
      <LoadingModal loading={isLoading} />
      <OtpNumberModal
        visible={showNumberModal}
        onPress={resendOtp}
        onPressCancel={() => setShowNumberModal(false)}
        title="फ़ोन नंबर चुनें"
        phoneNumbers={phone_numbers}
        maskedPhoneNumbers={displayPhoneNumbers}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: color.white,
  },
  innerContainer: {
    margin: 16,
    borderRadius: 16,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: color.black,
  },
  resendOtpContainer: {
    alignItems: "center",
    flexDirection: "row",
    justifyContent: "center",
  },
  resendOtpText: {
    paddingVertical: 16,
    paddingHorizontal: 2,
    color: color.blue,
    textDecorationLine: "underline",
  },
  phoneNumberText: {
    paddingVertical: 16,
    paddingHorizontal: 2,
    color: color.blue,
  },
  centeredContainer: {
    alignItems: "center",
  },
  rowCentered: {
    flexDirection: "row",
    justifyContent: "center",
  },
  mediumBlackText: {
    paddingVertical: 16,
    paddingHorizontal: 2,
    color: color.mediumBlacklightBlack,
  },
  resendActionText: {
    paddingVertical: 16,
    paddingHorizontal: 2,
    color: color.blue,
    textDecorationLine: "underline",
  },
  countdownText: {
    color: color.red,
  },
  headerWhiteBg: {
    backgroundColor: OTP_PALETTE.headerBg,
  },
  incentiveBannerWrapper: {
    alignItems: "center",
  },
  incentiveBanner: {
    width: width * 0.8,
    paddingVertical: 10,
    paddingHorizontal: 18,
    borderBottomLeftRadius: 18,
    borderBottomRightRadius: 18,
    backgroundColor: OTP_PALETTE.incentiveBannerBg,
  },
  otpRequestBtnContainer: {
    alignItems: "flex-start",
    marginLeft: 20,
    flex: 1,
    justifyContent: "flex-end",
    marginBottom: 20,
  },
  extraInfoText: {
    padding: 16,
    paddingVertical: 10,
    color: color.mediumBlacklightBlack,
    textAlign: "center",
  },
  markWithoutOtpContainer: {
    width: "100%",
    paddingHorizontal: 16,
    paddingBottom: 20,
    marginTop: 100,
    alignItems: "center",
  },
  markWithoutOtpButton: {
    minWidth: 220,
    paddingVertical: 12,
    paddingHorizontal: 20,
    backgroundColor: OTP_PALETTE.markWithoutOtpBg,
    borderRadius: 12,
    alignItems: "center",
    marginTop: 12,
    shadowColor: OTP_PALETTE.shadowColor,
    shadowOpacity: 0.02,
    shadowRadius: 2,
    shadowOffset: { width: 0, height: 1 },
    elevation: 1,
  },
  markWithoutOtpText: {
    color: color.primary,
    fontWeight: "700",
    textDecorationLine: "underline",
  },
});

export default NewOTPVerification;
